import { useEffect, useMemo, useState, type FormEvent } from 'react';

import type {
    AdminCreditPulseSubscriptionTier,
    AdminPulseSubscription,
    AdminPulseSubscriptionInput,
} from '../../../services/api';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Select } from '../../ui/select';
import { Actions, DialogFrame, Field, FormErrorAlert } from './creditDialogPrimitives';
import { getMutationErrorMessage } from './creditDialogHelpers';

interface PulseSubscriptionDialogProps {
    open: boolean;
    subscription: AdminPulseSubscription | null;
    companyName: string;
    teams: Array<{ id: number; name: string }>;
    tiers: AdminCreditPulseSubscriptionTier[];
    isSaving: boolean;
    onClose: () => void;
    onSave: (input: AdminPulseSubscriptionInput) => Promise<void>;
}

export function PulseSubscriptionDialog(_props: PulseSubscriptionDialogProps) {
    const { open, subscription, companyName, teams, tiers, isSaving, onClose, onSave } = _props;
    const allowedTiers = useMemo(
        () =>
            tiers.filter((tier) => tier.type === 'free' || tier.type === 'enterprise_placeholder'),
        [tiers],
    );
    const readOnly = Boolean(subscription?.sowId);
    const [scope, setScope] = useState<'company' | 'team'>('company');
    const [teamId, setTeamId] = useState('');
    const [tierId, setTierId] = useState('');
    const [startedAt, setStartedAt] = useState('');
    const [expiresAt, setExpiresAt] = useState('');
    const [totalCredits, setTotalCredits] = useState('');
    const [numSeats, setNumSeats] = useState('1');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) {
            return;
        }
        setScope(subscription?.scope ?? 'company');
        setTeamId(subscription?.teamId ? String(subscription.teamId) : '');
        setTierId(subscription?.pulseSubscriptionTierId ?? allowedTiers[0]?.id ?? '');
        setStartedAt(
            subscription?.startedAt
                ? toDateTimeLocalValue(subscription.startedAt)
                : toDateTimeLocalValue(new Date().toISOString()),
        );
        setExpiresAt(subscription?.expiresAt ? toDateTimeLocalValue(subscription.expiresAt) : '');
        setTotalCredits(
            subscription?.totalCredits != null ? String(subscription.totalCredits) : '',
        );
        setNumSeats(subscription?.numSeats !== undefined ? String(subscription.numSeats) : '1');
        setError(null);
    }, [allowedTiers, open, subscription]);

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setError(null);
        if (!startedAt) {
            setError('Start is required.');
            return;
        }
        if (!expiresAt) {
            setError('Expiration is required.');
            return;
        }
        if (!tierId) {
            setError('Pulse subscription type is required.');
            return;
        }
        if (scope === 'team' && !teamId) {
            setError('Team is required for team scope.');
            return;
        }
        const credits = Number(totalCredits);
        const seats = Number(numSeats);
        if (totalCredits.trim() === '' || !Number.isInteger(credits) || credits < 0) {
            setError('Total credits must be a non-negative whole number.');
            return;
        }
        if (numSeats.trim() === '' || !Number.isInteger(seats) || seats < 0) {
            setError('Number of seats must be a non-negative whole number.');
            return;
        }
        const startDate = new Date(startedAt);
        const expirationDate = new Date(expiresAt);
        if (startDate.getTime() >= expirationDate.getTime()) {
            setError('Expiration must be after the start date.');
            return;
        }
        try {
            await onSave({
                scope,
                teamId: scope === 'team' ? Number(teamId) : null,
                pulseSubscriptionTierId: tierId,
                startedAt: startDate.toISOString(),
                expiresAt: expirationDate.toISOString(),
                totalCredits: credits,
                numSeats: seats,
            });
        } catch (saveError) {
            setError(getMutationErrorMessage(saveError, 'Could not save Pulse subscription.'));
        }
    };

    return (
        <DialogFrame
            open={open}
            onClose={onClose}
            title={subscription ? 'Edit Pulse Subscription' : 'Create Pulse Subscription'}
            description={
                subscription
                    ? `Subscription ${subscription.id}`
                    : `Create a standalone subscription for ${companyName}.`
            }
        >
            <form className="space-y-4" onSubmit={handleSubmit}>
                {readOnly ? (
                    <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-900">
                        This subscription is managed by its SOW.
                    </p>
                ) : null}
                {error ? <FormErrorAlert message={error} /> : null}
                <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Scope" required>
                        <Select
                            ariaLabel="Scope"
                            value={scope}
                            disabled={readOnly}
                            options={[
                                { value: 'company', label: 'Company' },
                                { value: 'team', label: 'Team' },
                            ]}
                            onValueChange={(value) => {
                                setScope(value === 'team' ? 'team' : 'company');
                                if (value !== 'team') {
                                    setTeamId('');
                                }
                            }}
                        />
                    </Field>
                    {scope === 'team' ? (
                        <Field label="Team" required>
                            <Select
                                ariaLabel="Team"
                                value={teamId}
                                disabled={readOnly}
                                placeholder="Select a team"
                                options={teams.map((team) => ({
                                    value: String(team.id),
                                    label: team.name,
                                }))}
                                onValueChange={setTeamId}
                            />
                        </Field>
                    ) : (
                        <Field label="Company">
                            <Input value={companyName} disabled />
                        </Field>
                    )}
                    <Field label="Pulse subscription type" required>
                        <Select
                            ariaLabel="Pulse subscription type"
                            value={tierId}
                            disabled={readOnly}
                            options={allowedTiers.map((tier) => ({
                                value: tier.id,
                                label:
                                    tier.type === 'enterprise_placeholder' ? 'Enterprise' : 'Free',
                            }))}
                            onValueChange={setTierId}
                        />
                    </Field>
                    <Field label="Started at" required>
                        <Input
                            type="datetime-local"
                            disabled={readOnly}
                            value={startedAt}
                            onChange={(event) => setStartedAt(event.target.value)}
                        />
                    </Field>
                    <Field label="Expires at" required>
                        <Input
                            type="datetime-local"
                            disabled={readOnly}
                            value={expiresAt}
                            onChange={(event) => setExpiresAt(event.target.value)}
                        />
                    </Field>
                    <Field label="Total credits" required>
                        <Input
                            type="number"
                            min="0"
                            step="1"
                            disabled={readOnly}
                            value={totalCredits}
                            onChange={(event) => setTotalCredits(event.target.value)}
                        />
                    </Field>
                    <Field label="Number of seats" required>
                        <Input
                            type="number"
                            min="0"
                            step="1"
                            disabled={readOnly}
                            value={numSeats}
                            onChange={(event) => setNumSeats(event.target.value)}
                        />
                    </Field>
                </div>
                {readOnly ? (
                    <div className="flex justify-end pt-4">
                        <Button type="button" variant="outline" onClick={onClose}>
                            Close
                        </Button>
                    </div>
                ) : (
                    <Actions onCancel={onClose}>
                        <Button type="submit" disabled={isSaving}>
                            {isSaving
                                ? 'Saving…'
                                : subscription
                                  ? 'Save Changes'
                                  : 'Create Subscription'}
                        </Button>
                    </Actions>
                )}
            </form>
        </DialogFrame>
    );
}

function toDateTimeLocalValue(value: string): string {
    const date = new Date(value);
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
    return localDate.toISOString().slice(0, 16);
}
